import { useState, useMemo } from "react";

import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { useBlogPosts, useAllBlogPosts } from "@/shared/hooks/useBlogPosts";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { cn } from "@/shared/lib/utils";
import ArticleSearch from "./ArticleSearch";

const PAGE_SIZE = 6;

export default function BlogPosts() {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Fast initial load: first 6 articles without category enrichment
  const { data: initialData, isLoading, error } = useBlogPosts(PAGE_SIZE, 0, true);
  // Background load: all articles with categories
  const { data: allData } = useAllBlogPosts();

  const allLoaded = !!allData?.items;
  const items = allLoaded ? allData.items : (initialData?.items ?? []);

  const categories = useMemo(() => {
    if (!allData?.items) return [];

    const countMap = new Map<string, { name: string; count: number }>();
    for (const post of allData.items) {
      const cat = post.fieldData["primary-category-data"];
      if (cat?.name) {
        const existing = countMap.get(cat.name);
        if (existing) {
          existing.count++;
        } else {
          countMap.set(cat.name, { name: cat.name, count: 1 });
        }
      }
    }

    return Array.from(countMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allData?.items]);

  const filteredPosts = useMemo(() => {
    if (items.length === 0) return [];

    return items.filter((post) => {
      if (selectedCategory && post.fieldData["primary-category-data"]?.name !== selectedCategory) {
        return false;
      }

      if (search.trim()) {
        const query = search.toLowerCase();
        const name = post.fieldData.name.toLowerCase();
        const text = post.fieldData["plain-text"]?.toLowerCase() || "";
        if (!name.includes(query) && !text.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [items, search, selectedCategory]);

  const visiblePosts = useMemo(
    () => filteredPosts.slice(0, visibleCount),
    [filteredPosts, visibleCount],
  );

  const hasMore = visibleCount < filteredPosts.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-secondary">
        <Loader2 className="w-6 h-6 animate-spin" />
        <p className="ml-2">Loading articles</p>
      </div>
    );
  }

  if (error || items.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 mb-4">
        <p className="text-base font-medium flex-shrink-0">Articles</p>
        <ArticleSearch
          value={search}
          onChange={(value) => {
            setSearch(value);
            setVisibleCount(PAGE_SIZE);
          }}
        />
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => {
              setSelectedCategory(null);
              setVisibleCount(PAGE_SIZE);
            }}
            className={cn(
              "px-3 py-2 rounded-full text-sm border transition-colors",
              selectedCategory === null
                ? "bg-brand text-white"
                : "bg-white text-secondary border-primary",
            )}
          >
            All ({items.length})
          </button>
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => {
                setSelectedCategory(selectedCategory === cat.name ? null : cat.name);
                setVisibleCount(PAGE_SIZE);
              }}
              className={cn(
                "px-3 py-1.5 rounded-full text-sm border transition-colors",
                selectedCategory === cat.name
                  ? "bg-brand text-white"
                  : "bg-white text-secondary border-primary",
              )}
            >
              {cat.name} ({cat.count})
            </button>
          ))}
        </div>
      )}

      {filteredPosts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-secondary text-sm">No articles found matching your search.</p>
        </div>
      )}

      {visiblePosts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visiblePosts.map((post) => (
            <Link
              key={post.id}
              to={POST_LOSS_ROUTES.RESOURCES_ARTICLE.replace(":slug", post.fieldData.slug)}
              className="block no-underline"
            >
              <Card className="border-primary bg-white rounded-xl overflow-hidden cursor-pointer shadow-none transition-colors hover:bg-subtle h-full">
                <CardContent className="p-0 h-full">
                  <div className="flex overflow-hidden h-full">
                    {post.fieldData["main-image"]?.url && (
                      <div className="w-2/5 h-full relative overflow-hidden flex-shrink-0">
                        <img
                          src={post.fieldData["main-image"].url}
                          alt={post.fieldData.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="w-3/5 px-[16px] py-[24px] flex flex-col justify-center">
                      <h4 className="font-semibold text-sm text-primary mb-1">
                        {post.fieldData.name}
                      </h4>
                      <p className="text-sm text-stone-500 mb-2">
                        {format(new Date(post.lastPublished), "MMM d, yyyy")}
                      </p>
                      {post.fieldData["plain-text"] && (
                        <p className="text-sm text-stone-600 font-light line-clamp-2">
                          {post.fieldData["plain-text"]}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="flex justify-center mt-6">
          <Button variant="outline" onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}>
            Load more articles
          </Button>
        </div>
      )}
    </div>
  );
}
